interface JourneyStep {
  title: string;
  description: string;
  icon: JSX.Element;
}

const ICON_CLASS = 'w-6 h-6';

const STEPS: JourneyStep[] = [
  {
    title: 'Add your property',
    description: 'Add your properties and units in minutes',
    icon: (
      <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    ),
  },
  {
    title: 'Invite your tenant',
    description: 'Send a portal invite — tenants can pay, message, and track maintenance',
    icon: (
      <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
      </svg>
    ),
  },
  {
    title: 'Send the lease',
    description: 'Guided wizard with state clause reminders and built-in e-signatures',
    icon: (
      <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    ),
  },
  {
    title: 'Manage everything from one place',
    description: 'Rent, maintenance, expenses, and reporting — all in one dashboard',
    icon: (
      <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
      </svg>
    ),
  },
];

export function JourneyOverview(): JSX.Element {
  return (
    <section
      id="how-it-works"
      data-testid="landing-how-it-works"
      className="py-[var(--landing-section-py)] bg-[var(--landing-surface-alt)]"
    >
      <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--landing-text-primary)] mb-4">
            How it works
          </h2>
          <p className="text-lg text-[var(--landing-text-muted)] max-w-2xl mx-auto">
            From setting up your first property to managing everything from one dashboard — Resida
            covers every step.
          </p>
        </div>

        {/* Desktop: horizontal stepper */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-8">
          {STEPS.map((step, i) => (
            <div
              key={i}
              data-testid={`how-it-works-step-${i + 1}`}
              className="flex flex-col items-center text-center"
            >
              <div className="relative mb-3">
                <div className="w-12 h-12 rounded-full bg-[var(--landing-surface)] border border-[var(--landing-border)] flex items-center justify-center text-[var(--landing-text-muted)]">
                  {step.icon}
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className="absolute top-6 left-full w-full h-px bg-[var(--landing-border)]"
                    aria-hidden="true"
                  />
                )}
              </div>
              <span className="text-sm font-medium text-[var(--landing-text-primary)] mb-1">
                {step.title}
              </span>
              <span className="text-xs text-[var(--landing-text-muted)] leading-snug">
                {step.description}
              </span>
            </div>
          ))}
        </div>

        {/* Mobile/Tablet: vertical list */}
        <div className="lg:hidden space-y-4">
          {STEPS.map((step, i) => (
            <div
              key={i}
              className="flex items-start gap-4 p-4 rounded-[var(--landing-radius)] bg-[var(--landing-surface)] border border-[var(--landing-border)]"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--landing-bg)] flex items-center justify-center text-[var(--landing-text-muted)]">
                {step.icon}
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--landing-text-primary)]">
                  <span className="text-[var(--landing-text-muted)] mr-2">{i + 1}.</span>
                  {step.title}
                </div>
                <div className="text-xs text-[var(--landing-text-muted)] mt-0.5">
                  {step.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
